/**
 * Per-tab source request scheduler.
 *
 * All source metadata requests already flow through `proxyFetch`; this module
 * sits behind that boundary to smooth bursts before they reach the Worker and
 * to pause a source briefly when the upstream tells us to slow down.
 */

export type SourceRequestScope = 'nhentai' | 'ehentai' | 'exhentai' | 'hitomi' | 'unknown';

interface SchedulerConfig {
  maxConcurrent: number;
  baseCooldownMs: number;
  maxCooldownMs: number;
}

interface QueueTask<T> {
  run: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
  signal?: AbortSignal;
  abortHandler?: () => void;
}

interface SchedulerState {
  active: number;
  queue: QueueTask<unknown>[];
  cooldownUntil: number;
  throttleStreak: number;
  timer: ReturnType<typeof globalThis.setTimeout> | undefined;
}

const DEFAULT_CONFIGS: Record<SourceRequestScope, SchedulerConfig> = {
  nhentai: { maxConcurrent: 3, baseCooldownMs: 30_000, maxCooldownMs: 5 * 60_000 },
  ehentai: { maxConcurrent: 4, baseCooldownMs: 30_000, maxCooldownMs: 5 * 60_000 },
  exhentai: { maxConcurrent: 4, baseCooldownMs: 30_000, maxCooldownMs: 5 * 60_000 },
  hitomi: { maxConcurrent: 5, baseCooldownMs: 20_000, maxCooldownMs: 2 * 60_000 },
  unknown: { maxConcurrent: 3, baseCooldownMs: 30_000, maxCooldownMs: 5 * 60_000 },
};

let configs: Record<SourceRequestScope, SchedulerConfig> = cloneConfigs(DEFAULT_CONFIGS);

const states = new Map<SourceRequestScope, SchedulerState>();

export function sourceScopeFromUrl(url: string): SourceRequestScope {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'unknown';
  }
  const host = parsed.host.toLowerCase();
  if (host === 'nhentai.net' || host === 'www.nhentai.net') return 'nhentai';
  if (host === 'exhentai.org' || host === 's.exhentai.org') return 'exhentai';
  if (
    host === 'e-hentai.org' ||
    host === 'www.e-hentai.org' ||
    host === 'api.e-hentai.org' ||
    host === 'forums.e-hentai.org'
  ) {
    return 'ehentai';
  }
  if (
    host === 'hitomi.la' ||
    host.endsWith('.hitomi.la') ||
    host === 'gold-usergeneratedcontent.net' ||
    host.endsWith('.gold-usergeneratedcontent.net')
  ) {
    return 'hitomi';
  }
  return 'unknown';
}

export function scheduleSourceRequest<T>(
  scope: SourceRequestScope,
  run: () => Promise<T>,
  opts: { signal?: AbortSignal } = {},
): Promise<T> {
  if (opts.signal?.aborted) {
    return Promise.reject(abortError());
  }

  return new Promise<T>((resolve, reject) => {
    const state = stateFor(scope);
    const task: QueueTask<T> = { run, resolve, reject, signal: opts.signal };
    if (opts.signal) {
      task.abortHandler = () => {
        removeQueuedTask(state, task);
        reject(abortError());
      };
      opts.signal.addEventListener('abort', task.abortHandler, { once: true });
    }
    state.queue.push(task as QueueTask<unknown>);
    pump(scope);
  });
}

export function recordSourceResponse(scope: SourceRequestScope, response: Response): void {
  const cooldownMs = cooldownMsForResponse(scope, response);
  if (cooldownMs !== undefined) {
    applyCooldown(scope, cooldownMs);
    return;
  }
  if (response.ok) {
    const state = stateFor(scope);
    state.throttleStreak = 0;
  }
}

function cooldownMsForResponse(scope: SourceRequestScope, response: Response): number | undefined {
  const retryAfter = parseRetryAfter(response.headers.get('retry-after'));
  if (response.status === 429) {
    return retryAfter ?? backoffFor(scope);
  }
  if (response.status === 509) {
    return retryAfter ?? Math.min(backoffFor(scope) * 2, configs[scope].maxCooldownMs);
  }
  if (looksLikeCloudflareChallenge(response)) {
    return retryAfter ?? Math.min(backoffFor(scope) * 2, configs[scope].maxCooldownMs);
  }
  if (response.status === 503 && retryAfter !== undefined) {
    return retryAfter;
  }
  return undefined;
}

function looksLikeCloudflareChallenge(response: Response): boolean {
  const mitigated = response.headers.get('cf-mitigated');
  if (mitigated && mitigated.toLowerCase() === 'challenge') return true;
  if (response.status === 503 && (response.headers.get('server') ?? '').toLowerCase().includes('cloudflare')) {
    return true;
  }
  return false;
}

function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined;
  const seconds = Number.parseFloat(value);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.ceil(seconds * 1000);
  }
  const dateMs = Date.parse(value);
  if (!Number.isFinite(dateMs)) return undefined;
  return Math.max(0, dateMs - Date.now());
}

function backoffFor(scope: SourceRequestScope): number {
  const state = stateFor(scope);
  const config = configs[scope];
  const multiplier = 2 ** Math.min(state.throttleStreak, 5);
  return Math.min(config.baseCooldownMs * multiplier, config.maxCooldownMs);
}

function applyCooldown(scope: SourceRequestScope, cooldownMs: number): void {
  const state = stateFor(scope);
  const config = configs[scope];
  state.throttleStreak += 1;
  state.cooldownUntil = Math.max(
    state.cooldownUntil,
    Date.now() + Math.min(cooldownMs, config.maxCooldownMs),
  );
  pump(scope);
}

function pump(scope: SourceRequestScope): void {
  const state = stateFor(scope);
  const config = configs[scope];
  if (state.timer !== undefined) {
    globalThis.clearTimeout(state.timer);
    state.timer = undefined;
  }

  const now = Date.now();
  if (state.cooldownUntil > now) {
    state.timer = globalThis.setTimeout(() => {
      state.timer = undefined;
      pump(scope);
    }, state.cooldownUntil - now);
    return;
  }

  while (state.active < config.maxConcurrent && state.queue.length > 0) {
    const task = state.queue.shift();
    if (!task) break;
    if (task.signal?.aborted) {
      cleanupAbortHandler(task);
      task.reject(abortError());
      continue;
    }
    cleanupAbortHandler(task);
    state.active += 1;
    task
      .run()
      .then(task.resolve, task.reject)
      .finally(() => {
        state.active -= 1;
        pump(scope);
      });
  }
}

function stateFor(scope: SourceRequestScope): SchedulerState {
  let state = states.get(scope);
  if (!state) {
    state = {
      active: 0,
      queue: [],
      cooldownUntil: 0,
      throttleStreak: 0,
      timer: undefined,
    };
    states.set(scope, state);
  }
  return state;
}

function removeQueuedTask<T>(state: SchedulerState, task: QueueTask<T>): void {
  const idx = state.queue.indexOf(task as QueueTask<unknown>);
  if (idx !== -1) {
    state.queue.splice(idx, 1);
  }
}

function cleanupAbortHandler<T>(task: QueueTask<T>): void {
  if (task.signal && task.abortHandler) {
    task.signal.removeEventListener('abort', task.abortHandler);
    task.abortHandler = undefined;
  }
}

function abortError(): Error {
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
}

function cloneConfigs(
  source: Record<SourceRequestScope, SchedulerConfig>,
): Record<SourceRequestScope, SchedulerConfig> {
  return {
    nhentai: { ...source.nhentai },
    ehentai: { ...source.ehentai },
    exhentai: { ...source.exhentai },
    hitomi: { ...source.hitomi },
    unknown: { ...source.unknown },
  };
}

export function __resetSourceSchedulerForTests(): void {
  for (const state of states.values()) {
    if (state.timer !== undefined) {
      globalThis.clearTimeout(state.timer);
    }
  }
  states.clear();
  configs = cloneConfigs(DEFAULT_CONFIGS);
}

export function __configureSourceSchedulerForTests(
  overrides: Partial<Record<SourceRequestScope, Partial<SchedulerConfig>>>,
): void {
  const next = cloneConfigs(DEFAULT_CONFIGS);
  for (const [scope, override] of Object.entries(overrides) as Array<
    [SourceRequestScope, Partial<SchedulerConfig>]
  >) {
    next[scope] = { ...next[scope], ...override };
  }
  configs = next;
}
